import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PasswordExpiryStatus {
  expired: boolean;
  warning: boolean;
  daysUntilExpiry: number | null;
  expiresAt: string | null;
  message: string | null;
}

interface PasswordExpiryResponse {
  expired?: boolean;
  warning?: boolean;
  days_until_expiry?: number;
  expires_at?: string;
  message?: string;
}

interface UsePasswordExpiryReturn {
  status: PasswordExpiryStatus | null;
  loading: boolean;
  checkExpiry: () => Promise<void>;
  recordPasswordChange: () => Promise<void>;
}

export function usePasswordExpiry(userId: string | undefined): UsePasswordExpiryReturn {
  const [status, setStatus] = useState<PasswordExpiryStatus | null>(null);
  const [loading, setLoading] = useState(false);

  const checkExpiry = useCallback(async () => {
    if (!userId) {
      setStatus(null);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('check_password_expiry', {
        p_user_id: userId
      });

      if (error) {
        logger.error('[PasswordExpiry] Check failed:', error);
        setStatus(null);
        return;
      }

      // Type assertion for the JSONB response
      const response = data as PasswordExpiryResponse | null;

      if (response) {
        setStatus({
          expired: response.expired ?? false,
          warning: response.warning ?? false,
          daysUntilExpiry: response.days_until_expiry ?? null,
          expiresAt: response.expires_at ?? null,
          message: response.message ?? null
        });
      }
    } catch (e) {
      logger.error('[PasswordExpiry] Unexpected error:', e);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const recordPasswordChange = useCallback(async () => {
    if (!userId) return;

    try {
      const { error } = await supabase.rpc('record_password_change', {
        p_user_id: userId
      });

      if (error) {
        logger.error('[PasswordExpiry] Failed to record password change:', error);
        return;
      }

      // Optimistically update local status to non-expired
      // instead of re-fetching which could re-trigger the dialog
      setStatus(prev => prev ? {
        ...prev,
        expired: false,
        warning: false,
        message: null,
      } : null);
    } catch (e) {
      logger.error('[PasswordExpiry] Unexpected error:', e);
    }
  }, [userId]);

  useEffect(() => {
    checkExpiry();
  }, [checkExpiry]);

  return {
    status,
    loading,
    checkExpiry,
    recordPasswordChange
  };
}
